/**
 * MorrowOS sub-agent detail — compact activity timeline with optional
 * technical payload expansion. Embedded inside {@link MorrowContextPanel}
 * below the action cards and app summaries.
 */
import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";
import {
  MORROW_MAIN_AGENT_ID,
  morrowAgentSummary,
  previewToolValue,
} from "@t3tools/client-runtime/state/morrowAgentRuntime";
import { ChevronDown, ChevronRight } from "~/icons/solar-lucide";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { useI18n } from "~/i18n";
import { cuteAgentName } from "../morrowAgentDisplay";
import { MorrowAgentOrb } from "./brand/MorrowAgentOrb";
import { localizeToolCallDescription } from "./chat/toolCallDescriptionI18n";
import { describeToolCall } from "./chat/workEntryStep";
import { WorkEntryStepIcon } from "./chat/WorkEntryStepIcon";
import {
  morrowToolCallLifecycleState,
  WorkStepLifecycleIndicator,
} from "./chat/WorkStepLifecycleIndicator";
import ChatMarkdown from "./ChatMarkdown";
import { formatToolCallInputBrief, formatToolCallResultBrief } from "./morrowToolCallBrief";

function morrowAgentDisplayLabel(agent: Pick<MorrowAgent, "agentId" | "label">): string {
  if (agent.agentId === MORROW_MAIN_AGENT_ID) return "Morrow";
  return /^subagent-/i.test(agent.label) ? cuteAgentName(agent.agentId) : agent.label;
}

function StatusDot({ status }: { status: MorrowAgent["status"] }) {
  if (status === "running") {
    return <WorkStepLifecycleIndicator state="inProgress" size="sm" />;
  }
  if (status === "failed") {
    return <WorkStepLifecycleIndicator state="failed" size="sm" />;
  }
  return <WorkStepLifecycleIndicator state="completed" size="sm" />;
}

function ToolCallRow({ call }: { call: MorrowAgent["toolCalls"][number] }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const input = previewToolValue(call.input);
  const result = previewToolValue(call.result);
  const desc = localizeToolCallDescription(describeToolCall(call.toolName), call.toolName, t);
  const title = desc.appName ? `${desc.appName} · ${desc.action}` : desc.action;
  const inputBrief = formatToolCallInputBrief(call);
  const resultBrief = formatToolCallResultBrief(call);
  const subtitle = [inputBrief, resultBrief].filter(Boolean).join(" · ");
  const hasTechnical = Boolean(input || result);

  return (
    <div className="rounded-xl border border-border/50 bg-background/60">
      <button
        type="button"
        className={cn(
          "flex w-full items-start gap-2.5 px-2.5 py-2 text-left",
          hasTechnical && "cursor-pointer hover:bg-muted/30",
        )}
        onClick={() => {
          if (hasTechnical) setExpanded((value) => !value);
        }}
        aria-expanded={hasTechnical ? expanded : undefined}
        disabled={!hasTechnical}
      >
        <WorkStepLifecycleIndicator
          state={morrowToolCallLifecycleState(call.status)}
          size="sm"
          className="mt-1.5"
        />
        <WorkEntryStepIcon
          heading={title}
          toolSlug={call.toolName}
          appName={desc.appName}
          logoUrl={desc.logoUrl}
          className="mt-0.5 size-4"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-foreground">{title}</span>
          {subtitle ? (
            <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
              {subtitle}
            </span>
          ) : null}
        </span>
        {hasTechnical ? (
          expanded ? (
            <ChevronDown aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : null}
      </button>
      {expanded && hasTechnical ? (
        <div className="space-y-2 border-t border-border/40 px-2.5 py-2">
          {input ? (
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                Input
              </div>
              <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted-foreground">
                {input}
              </pre>
            </div>
          ) : null}
          {result ? (
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                Result
              </div>
              <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-foreground/80">
                {result}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Agent roster + activity timeline — rendered inside the merged context panel. */
export function MorrowAgentDetailSection({
  agents,
  selectedAgentId,
}: {
  agents: ReadonlyArray<MorrowAgent>;
  selectedAgentId: string | null;
}) {
  if (agents.length === 0) return null;

  // SHOW WHOEVER IS ACTUALLY WORKING. `agents[0]` is the orchestrator, and while it delegates it
  // has nothing to show — so a browser sub-agent could be driving a page, step by step, with the
  // panel sitting on "Planning & delegating the work" and the live steps one click away behind a
  // roster nobody knew to open. Falling back to the running sub-agent puts the work on screen; an
  // explicit click still wins, so this only chooses when the user has not.
  const running = agents.filter(
    (agent) => agent.status === "running" && agent.agentId !== MORROW_MAIN_AGENT_ID,
  );
  const liveliest =
    running.length > 0
      ? running.reduce((newest, agent) => (agent.updatedAt > newest.updatedAt ? agent : newest))
      : null;
  const selected =
    agents.find((agent) => agent.agentId === selectedAgentId) ?? liveliest ?? agents[0] ?? null;
  if (!selected) return null;

  const reasoningNotes = selected.notes.filter((note) => note.role === "reasoning");
  const showRoster = agents.length > 1;

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <MorrowAgentOrb className="size-3.5 text-muted-foreground" aria-hidden />
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Agent activity
        </p>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="rounded-2xl border border-border/50 bg-card/40 p-3">
          <div className="flex items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground">
              <MorrowAgentOrb className="size-3.5" />
            </span>
            <span className="min-w-0 truncate text-sm font-medium">
              {morrowAgentDisplayLabel(selected)}
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
              <StatusDot status={selected.status} />
              <span>
                {selected.status === "running"
                  ? "Working"
                  : selected.status === "failed"
                    ? "Failed"
                    : // "Done" only when the agent SAID so (it emits an answer note). Claiming it
                      // because no tool happens to be in flight turned every gap between two tool
                      // calls into a finished badge on an agent that was still working.
                      selected.finalAnswer !== null
                      ? "Done"
                      : "No summary"}
              </span>
            </span>
          </div>
          <p className="mt-1.5 text-[12px] leading-5 text-foreground/80">
            {selected.task ?? morrowAgentSummary(selected)}
          </p>
          {showRoster ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              {agents.length} agents in this thread
            </p>
          ) : null}
        </div>

        {selected.toolCalls.length > 0 ? (
          <div className="flex flex-col gap-2">
            {selected.toolCalls.map((call) => (
              <ToolCallRow key={call.id} call={call} />
            ))}
          </div>
        ) : null}

        {reasoningNotes.length > 0 ? (
          <details className="rounded-xl border border-border/40 bg-card/20 px-2.5 py-2">
            <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Reasoning ({reasoningNotes.length})
            </summary>
            <div className="mt-2 flex flex-col gap-2">
              {reasoningNotes.map((note) => (
                <div
                  key={note.id}
                  className="min-w-0 break-words text-[12px] leading-5 text-muted-foreground"
                >
                  <ChatMarkdown text={note.text} cwd={undefined} />
                </div>
              ))}
            </div>
          </details>
        ) : null}

        {selected.toolCalls.length === 0 && reasoningNotes.length === 0 && !selected.finalAnswer ? (
          <p className="text-[12px] text-muted-foreground">Starting up…</p>
        ) : null}
      </div>
    </section>
  );
}

/** Standalone panel wrapper — kept for narrow imports; chat uses the section inside context. */
export function MorrowAgentDetailPanel({
  agents,
  selectedAgentId,
}: {
  agents: ReadonlyArray<MorrowAgent>;
  selectedAgentId: string | null;
  onSelectAgent?: (agentId: string) => void;
}) {
  if (agents.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <MorrowAgentOrb className="size-6" />
        <p className="text-sm font-medium">No sub-agents yet</p>
        <p className="max-w-56 text-xs text-muted-foreground">
          When Morrow spawns sub-agents for a task, their activity shows up here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-4">
      <MorrowAgentDetailSection agents={agents} selectedAgentId={selectedAgentId} />
    </div>
  );
}
